import { Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAdoption } from "@/hooks/useAdoption";

interface AdoptionWidgetProps {
  /** Project slug to scope adoption stats to. Omitted = org scope. */
  projectSlug?: string;
}

/**
 * Usage + quality pair (RAISE-16695, AC5) — the epic's anti-vanity guard:
 * a "Team Adoption" card can't ship without also showing whether that
 * adoption produced governed, first-pass-successful work. Kept as ONE card
 * (not two siblings) so the pair can't be rearranged apart.
 */
export function AdoptionWidget({ projectSlug }: AdoptionWidgetProps = {}) {
  const { t } = useTranslation();
  const { data, loading } = useAdoption(projectSlug);

  // R3 structural guard: a quality-less payload (contract violation — stale
  // msw fixture, old server) renders nothing rather than a usage-only card.
  if (loading || !data || !data.quality) return null;

  const { quality } = data;
  // Tightened from `total_members === 0` (S5197.7): an org/project with
  // gate data but no countable members should still show the pair.
  if (data.total_members === 0 && quality.sample_size === 0) return null;

  const pct =
    data.total_members > 0
      ? Math.round((data.active_members / data.total_members) * 100)
      : 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{t("adoption.title")}</CardTitle>
        <Users className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {/* Usage half */}
        <div className="text-2xl font-bold">
          {data.active_members}{" "}
          <span className="text-base font-normal text-muted-foreground">
            {t("adoption.activeOf")} {data.total_members}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          {t("adoption.activeThisWeek")}
        </p>
        <div className="h-2 w-full rounded-full bg-muted">
          <div
            className="h-2 rounded-full bg-primary transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="mt-1 text-right text-xs text-muted-foreground">{pct}%</p>

        {/* Quality half */}
        <div className="mt-4 border-t pt-3">
          <p className="text-xs font-medium text-muted-foreground">
            {t("adoption.quality.title")}
          </p>
          {quality.sample_size === 0 ? (
            <div className="mt-1">
              <p className="text-sm text-muted-foreground">
                {t("adoption.quality.empty")}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("adoption.quality.emptyDetail")}
              </p>
            </div>
          ) : (
            <>
              <div className="text-2xl font-bold">
                {quality.first_pass_gate_success_rate !== null
                  ? `${Math.round(quality.first_pass_gate_success_rate * 100)}%`
                  : "—"}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("adoption.quality.sampleSize", { count: quality.sample_size })}
              </p>
            </>
          )}
          {quality.hitl_sample_size > 0 && quality.acceptance_rate !== null && (
            <p
              className="mt-2 text-xs text-muted-foreground"
              title={t("adoption.quality.acceptanceHint")}
            >
              <span>{t("adoption.quality.acceptanceLabel")}</span>{" "}
              <span>{Math.round(quality.acceptance_rate * 100)}%</span>
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
